import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useInfiniteQuery } from "@tanstack/react-query";

import { amountColumnHeader, useCurrency } from "../lib/currency";
import { formatDate } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { LoadMoreButton } from "./LoadMoreButton";
import { TableMessageRow } from "./TableMessageRow";

const COLUMNS = 8;

/** Every battle the user held a seat in, from `users.battles`, with that seat's outcome. */
export function UserBattlesTab({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const query = useInfiniteQuery(
    trpc.users.battles.infiniteQueryOptions(
      { endUserId, limit: 25 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Created</TableHead>
            <TableHead>Mode</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Seats</TableHead>
            <TableHead className="text-right">{amountColumnHeader("Price", currency)}</TableHead>
            <TableHead>Result</TableHead>
            <TableHead className="text-right">{amountColumnHeader("Won", currency)}</TableHead>
            <TableHead>Completed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isLoading && <TableMessageRow colSpan={COLUMNS}>Loading…</TableMessageRow>}
          {query.error && (
            <TableMessageRow colSpan={COLUMNS} tone="error">
              {query.error.message}
            </TableMessageRow>
          )}
          {query.isSuccess && rows.length === 0 && (
            <TableMessageRow colSpan={COLUMNS}>No battles yet.</TableMessageRow>
          )}
          {rows.map((battle) => {
            const seat = battle.seat;
            const decided = battle.status === "completed" && seat?.isWinner !== null;
            return (
              <TableRow key={battle.id}>
                <TableCell className="text-muted-foreground">
                  {formatDate(battle.createdAt)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{battle.mode}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={battle.status === "completed" ? "default" : "secondary"}>
                    {battle.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {battle.seatsFilled}/{battle.playerCount}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatAmount(battle.priceCents, currency)}
                </TableCell>
                <TableCell>
                  {!seat ? (
                    <span className="text-muted-foreground">—</span>
                  ) : !decided ? (
                    <span className="text-muted-foreground">pending</span>
                  ) : seat.isWinner ? (
                    <Badge variant="default">won</Badge>
                  ) : (
                    <Badge variant="outline">lost</Badge>
                  )}
                  {seat && seat.claimStatus !== "none" && (
                    <span className="ml-2 text-xs text-muted-foreground">{seat.claimStatus}</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {seat?.totalValueCents === null || seat?.totalValueCents === undefined
                    ? "—"
                    : formatAmount(seat.totalValueCents, currency)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {battle.completedAt ? formatDate(battle.completedAt) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <LoadMoreButton
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </>
  );
}
